import React from 'react';
import {Note, NoteFormData} from "types/Note";
import {Form, Formik, FormikHelpers} from "formik";
import Button from "components/Button";
import {useNavigate} from "react-router-dom";
import Field from "components/Form/Field";
import {DateTime} from "luxon";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import MultiSelector from "components/Form/MultiSelector";
import Toggle from "components/Form/Toggle";
import Selector from "components/Form/Selector";
import IconName from "types/IconName";
import {Item} from 'components/Form/MultiSelector/MultiSelector';
import FieldHeader from "components/Form/FieldHeader";
import {DateField} from "../../../components/Form/Form/Field";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import * as Yup from "yup";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";

async function trailOptions(): Promise<Array<Item>> {
  const trails = (await ngage.trails.filtered({page: 1, perPage: 200}))?.data ?? []

  return trails.map(trail => ({
    type: 'trail',
    id: `trail-${trail.id}`,
    icon: IconName.MAP,
    name: trail.name,
    description: `${trail.rating?.name ?? 'Unrated'} Trail`,
  }));
}

async function exhibitOptions(): Promise<Array<Item>> {
  const exhibits = (await ngage.exhibits.filtered({page: 1, per_page: 500}))?.data ?? []

  return exhibits.map(exhibit => ({
    type: 'exhibit',
    id: `exhibit-${exhibit.id}`,
    icon: IconName.LIBRARY,
    name: exhibit.name,
    description: exhibit.type.name,
  }))
}

async function elementOptions(): Promise<Array<Item>> {
  const elements = (await ngage.elements.all({page: 1, perPage: 200}))?.data ?? []

  return elements.map(element => ({
    type: 'element',
    id: `element-${element.id}`,
    icon: IconName.PUZZLE,
    name: element.name,
    description: element.exhibit? `Element of ${element.exhibit.name}` : element.description,
  }))
}

async function facilityOptions(): Promise<Array<Item>> {
  const facilities = (await ngage.facilities.filtered({page: 1, per_page: 200}))?.data ?? []

  return facilities.map(facility => ({
    type: 'facility',
    id: `facility-${facility.id}`,
    icon: facility.type.icon,
    name: facility.name,
    description: facility.type.name,
  }))
}

async function options(): Promise<Array<Item>> {
  return (await Promise.all([exhibitOptions(), elementOptions(), trailOptions(), facilityOptions()]))
    .flat()
    .sort((left, right) => left.name.localeCompare(right.name));
}

type NoteFormProps = {
  note?: Note
  onSubmit: (values: NoteFormData, helpers: FormikHelpers<NoteFormData>) => any
  onCancel?: (helpers?: FormikHelpers<NoteFormData>) => any
}

function NoteForm({note, onCancel, onSubmit}: NoteFormProps) {
  const navigate = useNavigate();
  const isUpdating = !!note;

  const {data: content, isLoading: isLoadingContent} = useQuery(
    ['note-content.all'],
    () => options()
  )

  function selectNotable(itemId: string, formik: FormikHelpers<NoteFormData>) {
    const [type, id] = itemId.split('-');

    void formik.setFieldValue('notable', {type, id: Number(id)});
  }

  function selectNotables(items: Array<Item> = [], formik: FormikHelpers<NoteFormData>) {
    void formik.setFieldValue('notables', items.map(item => {
      const [type, id] = String(item.id).split('-');

      return {type, id};
    }));
  }

  function validationSchema(values: NoteFormData) {
    const base = {
      title: Yup.string().required("This field is required").max(255, "Maximum character limit exceeded"),
      body: Yup.string().required("This field is required").max(50000, "Maximum character limit exceeded"),
    }

    const notables = Yup.array().of(Yup.object().shape({
      type: Yup.string(),
      id: Yup.number(),
    })).min(1, "This field is required")

    const notable = Yup.object().shape({
      type: Yup.string().required("This field is required"),
      id: Yup.number().required("This field is required"),
    })

    if (values.mode === 'create') {
      return Yup.object().shape({
        ...base,
        notables,
      });
    } else {
      return Yup.object().shape({
        ...base,
        notable,
      });
    }
  }

  let initialValues: NoteFormData;

  if (isUpdating) {
    // Updating an existing note: convert notable to notables array
    initialValues = {
      mode: 'update',
      title: note.title,
      body: note.body,
      is_important: note.is_important,
      expires_at: note.expires_at?.toUTC().toISO() ?? '',
      notable: { type: note.notable_type, id: note.notable?.id ?? 0 },
    };
  } else {
    // Creating a new note
    initialValues = {
      mode: 'create',
      title: '',
      body: '',
      is_important: false,
      expires_at: '',
      notables: [],
    };
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema(initialValues)} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {isUpdating ? 'Update Note' : 'New Note' }
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Additional information relevant to content that is time appropriate. These
                        can be set to expire, at which point they are automatically removed.
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        {isUpdating ?
                          <>
                            <Selector
                              label='Subject Content *'
                              items={content ?? []}
                              isLoading={isLoadingContent}
                              onChange={item => selectNotable(String(item.id), formik)}
                              selectedId={note.notable ? `${note.notable_type}-${note.notable.id}` : null}
                            />

                            {/* @ts-ignore */}
                            {(formik.values.mode === 'update' && !!formik.errors?.notable && !!formik.touched?.notable) && (
                              /* @ts-ignore */
                              <FieldHelp isError>
                                {/* @ts-ignore */}
                                {formik.errors.notable}
                              </FieldHelp>
                          )}
                          </> :
                          <>
                            <MultiSelector
                              label='Subject Content *'
                              items={content ?? []}
                              isLoading={isLoadingContent}
                              onChange={items => selectNotables(items, formik)}
                            />

                            {/* @ts-ignore */}
                            {(formik.values.mode === 'create' && !!formik.errors.notables && !!formik.touched?.notables) && (
                                /* @ts-ignore */
                                <FieldHelp isError>
                                  {/* @ts-ignore */}
                                  {formik.errors.notables}
                                </FieldHelp>
                              )
                            }
                          </>
                        }
                      </div>

                      <div className="sm:col-span-6">
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="title"
                            id="title"
                            label='Title *'
                            hint='255 chars max'
                            autoComplete="title"
                            maxLength="255"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <div className="mt-1">
                          <TextAreaField
                            label='Body *'
                            hint='50,000 chars max'
                            rows={6}
                            name="body"
                            id="body"
                            autoComplete="off"
                            maxChars={50000}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <FieldHeader label="Expires At" hint="optional" fieldId="expires_at"/>
                        <div className="mt-1">
                          <DateField
                            type='datetime-local'
                            name='expires_at'
                            id='expires_at'
                            autoComplete='off'
                            min={DateTime.now().plus({minute: 1}).toISO()}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <Toggle
                          label="Important"
                          name="is_important"
                          checked={formik.values.is_important}
                          onChange={(checked) => formik.setFieldValue('is_important', checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 py-5 bg-gray-50 text-right sm:px-6 sm:rounded-b-md">
                <div className="flex space-x-3 justify-end">
                  <Button
                    color={Button.colors.WHITE}
                    onClick={() => onCancel ? onCancel(formik) : navigate(-1)}
                    disabled={formik.isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    showSpinner={formik.isSubmitting}
                    disabled={formik.isSubmitting}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default NoteForm;